import {useTranslation} from '@aboutyou/i18n';
import React, {useRef} from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {Button} from '../../atoms/Buttons/Button';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {Text} from '../../atoms/Text/Text';
import {
  SR_QUEST_TEST_ID,
  SearchResultQuestionnaireWidgetProps,
} from './SearchResultQuestionnaireWidget';

export const GenericStep: React.FC<
  Partial<SearchResultQuestionnaireWidgetProps> & {
    className?: string;
    dataTestId: string;
  }
> = ({
  dataTestId,
  className,
  step,
  onFirstOptionSelected,
  onSecondOptionSelected,
  onSaveAndCloseClick,
  onClose,
}) => {
  const {t} = useTranslation();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  return (
    <Wrapper className={className} data-testid={dataTestId}>
      <TitleWrapper>
        <Title>
          {step === 'optionSelection'
            ? t("Why isn't this helpful?")
            : t('What else did you expect?')}
        </Title>
        <CloseIcon
          data-testid={`${SR_QUEST_TEST_ID}-closeButton`}
          onClick={onClose}
          asset={GenericIconName.Close}
          size="S"
        />
      </TitleWrapper>

      <GenericStepWrapper>
        {step === 'optionSelection' && (
          <>
            <StyledButton
              data-testid={`${SR_QUEST_TEST_ID}-opt1`}
              variant="secondary"
              onClick={onFirstOptionSelected}
            >
              {t("I can't find what I'm looking for")}
            </StyledButton>
            <StyledButton
              data-testid={`${SR_QUEST_TEST_ID}-opt2`}
              variant="secondary"
              onClick={onSecondOptionSelected}
            >
              {t('I expected something else')}
            </StyledButton>
          </>
        )}

        {step === 'feedbackInput' && (
          <>
            <Textarea
              data-testid={`${SR_QUEST_TEST_ID}-textarea`}
              ref={textAreaRef}
              placeholder={t('max. 50 char.')}
              maxLength={50}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            />
            <FeedbackInputWrapper>
              <Text size="px10" color="Grey1">
                {t('Optional - we already saved your feedback.')}
              </Text>
              <SaveAndClose
                data-testid={`${SR_QUEST_TEST_ID}-saveAndCloseButton`}
                variant="secondary"
                onClick={() =>
                  onSaveAndCloseClick?.(textAreaRef.current?.value ?? '')
                }
              >
                {t('Save & close')}
              </SaveAndClose>
            </FeedbackInputWrapper>
          </>
        )}
      </GenericStepWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-width: 303px;
  padding: 16px;
  box-sizing: border-box;
`;

const TitleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 40px;
  margin-bottom: 8px;
  padding: 0 32px;
`;

const Title = styled.div`
  font-size: ${FONT_SIZES.px14};
  line-height: ${FONT_SIZES.px20};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  white-space: nowrap;
`;

const CloseIcon = styled(Icon)`
  position: absolute;
  right: 0;
  cursor: pointer;
`;

const GenericStepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 8px;
`;

const StyledButton = styled(Button)`
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
`;

const Textarea = styled.textarea`
  width: 100%;
  height: 50px;
  padding: 11px 16px;
  background: ${COLORS.Grey4};
  font-size: ${FONT_SIZES.px12};
  color: ${COLORS.Black};
  border: none;
  resize: none;
  box-sizing: border-box;
  outline: none;

  &::placeholder {
    color: ${COLORS.Grey1};
  }
`;

const FeedbackInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  span {
    flex: 0 0 50%;
    line-height: ${FONT_SIZES.px16};
  }
`;

const SaveAndClose = styled(StyledButton)`
  &&& {
    flex: 1;
    padding: 0 12px;
  }
`;
